import api from './api';

export interface LLMModelConfig {
  model_id: string;
  model_host: string;
}

export interface LLMModelConfigWithSource extends LLMModelConfig {
  source: 'client_configured' | 'global_default';
}

export interface ClientLLMConfiguration {
  fast_LLM: LLMModelConfig;
  slow_LLM: LLMModelConfig;
  long_context_LLM: LLMModelConfig;
}

export interface ClientLLMConfigResponse {
  client_id: string;
  client_name: string;
  llm_configuration: {
    fast_LLM: LLMModelConfigWithSource;
    slow_LLM: LLMModelConfigWithSource;
    long_context_LLM: LLMModelConfigWithSource;
  };
  is_configured: boolean;
  applies_to: string;
}

export interface ClientLLMUpdateResponse {
  success: boolean;
  message: string;
  client_id: string;
  llm_configuration: ClientLLMConfiguration;
}

export interface DefaultLLMConfigResponse {
  default_llm_configuration: {
    fast_LLM: LLMModelConfig;
    slow_LLM: LLMModelConfig;
    long_context_LLM: LLMModelConfig;
  };
  is_configured: boolean;
  applies_to: string;
}

export interface DefaultLLMUpdateResponse {
  success: boolean;
  message: string;
  default_llm_configuration: {
    fast_LLM: LLMModelConfig;
    slow_LLM: LLMModelConfig;
    long_context_LLM: LLMModelConfig;
  };
}

export interface FunctionLLMConfigResponse {
  function_llm_config: Record<string, LLMModelConfig>;
  is_configured: boolean;
}

export interface LLMModel {
  record_id: string;
  model_id: string;
  model_name: string;
  model_host: string;
  context_size: number;
  pricing: {
    billing_type: string;
    [key: string]: unknown;
  };
  is_active: boolean;
}

export const clientLLMService = {
  async getClientLLMConfig(clientId: string): Promise<ClientLLMConfigResponse> {
    const response = await api.get(`/clients/${clientId}/llm-config`);
    return response.data;
  },

  async updateClientLLMConfig(
    clientId: string,
    config: ClientLLMConfiguration
  ): Promise<ClientLLMUpdateResponse> {
    const response = await api.put(`/clients/${clientId}/llm-config`, config);
    return response.data;
  },

  async getDefaultLLMConfig(): Promise<DefaultLLMConfigResponse> {
    const response = await api.get('/settings/default-llm-config');
    return response.data;
  },

  async updateDefaultLLMConfig(
    config: ClientLLMConfiguration
  ): Promise<DefaultLLMUpdateResponse> {
    const response = await api.put('/settings/default-llm-config', config);
    return response.data;
  },

  async getAvailableModels(): Promise<LLMModel[]> {
    const response = await api.get('/token-usage/models');
    return response.data.models;
  },

  async getFunctionLLMConfig(): Promise<FunctionLLMConfigResponse> {
    const response = await api.get('/settings/function-llm-config');
    return response.data;
  },

  async updateFunctionLLMConfig(
    config: Record<string, LLMModelConfig>
  ): Promise<{ success: boolean; message: string; function_llm_config: Record<string, LLMModelConfig> }> {
    const response = await api.put('/settings/function-llm-config', config);
    return response.data;
  },
};
